/* All automatic cart writes go through this bounded, idempotent reconciler. */
(function (root, factory) {
  const Core = typeof module === 'object' && module.exports ? require('./gift-promotion-core.js') : root.PMGiftCore;
  const Controller = factory(Core);
  if (typeof module === 'object' && module.exports) module.exports = Controller;
  else root.PMGiftController = Controller;
})(typeof window === 'undefined' ? globalThis : window, function (C) {
  'use strict';
  return class GiftController {
    constructor(ports) {
      this.ports = ports;
      this.memory = new Map();
      this.failures = new Map();
      this.running = null;
      this.dirty = false;
    }
    request(invalidate = true) {
      // Price follow-ups/polling can join current work without cancelling its stock probe.
      if (!invalidate && (this.running || this.reconciling)) return this.running || Promise.resolve(this.latest);
      this.dirty = true;
      if (!this.running) this.running = (this.ports.exclusive ? this.ports.exclusive(() => this.drain()) : this.drain())
        .finally(() => { this.running = null; });
      return this.running;
    }
    // Caller already owns the shared cart transaction. Reacquiring it would deadlock.
    // Defer section rendering so the customer never sees the intermediate charged excess gift.
    async settleInTransaction(cart) {
      this.dirty = true;
      return this.drain(false, cart);
    }
    async drain(refreshCart = true, cart) {
      this.reconciling = true;
      try {
        let cycles = 0;
        while (this.dirty && cycles++ < 6) {
          this.dirty = false;
          try { await this.reconcile(refreshCart, cart); cart = undefined; }
          catch (error) { this.ports.onError(error); throw error; }
        }
        if (this.dirty) throw new Error('Cart is still changing. Please try again.');
        return this.latest;
      } finally { this.reconciling = false; }
    }
    load(key) {
      if (this.memory.has(key)) return this.memory.get(key);
      let record;
      try { record = this.ports.load(key); } catch (_) { /* Storage may be unavailable. */ }
      const ledger = { assignments: { ...(record?.assignments || {}) }, waived: { ...(record?.waived || {}) } };
      this.memory.set(key, ledger);
      return ledger;
    }
    persist() { try { this.ports.save(this.key, this.ledger); } catch (_) { /* Keep in-memory history. */ } }
    async waive(slot) {
      if (!this.ledger) await this.request();
      if (!this.ledger || !Number.isInteger(slot) || slot < 1) return;
      this.ledger.waived[slot] = true;
      this.persist();
      return this.request();
    }
    async waiveSlots(slots) {
      if (!this.ledger) await this.request();
      if (!this.ledger) return;
      for (const slot of slots) if (Number.isInteger(slot) && slot > 0) this.ledger.waived[slot] = true;
      this.persist();
      return this.request();
    }
    async restore() {
      if (!this.ledger) await this.request();
      if (!this.ledger) return;
      this.ledger.waived = {};
      this.failures.clear();
      this.persist();
      return this.request();
    }
    retry() { this.priceWait = null; this.failures.clear(); return this.request(); }
    planBatch(cart, config, rule, candidates) {
      if (!candidates.length) return [];
      const planned = [], projected = { ...cart, items: [...cart.items] };
      // Bound payload size while keeping distinct slot properties, even for the same SKU.
      while (planned.length < 100) {
        const action = C.nextAction(projected, config, rule, this.ledger, candidates);
        if (action.type !== 'add') break;
        const selected = candidates.find(c => String(c.id) === String(this.ledger.assignments[action.slot])) ||
          candidates[C.randomIndex(candidates.length, this.ports.random)];
        const item = { id: selected.id, quantity: 1, properties: {
          _gift_source: C.SOURCE, _gift_campaign: config.campaign,
          _gift_slot: String(action.slot), _gift_rule: rule.key
        } };
        planned.push(item);
        projected.items.push({ ...item, variant_id: item.id, original_line_price: 0, final_line_price: 0 });
      }
      return planned;
    }
    emit(cart, config, rule, status = '') {
      const summary = C.state(cart, rule, config.maxGifts, config);
      const { gifts, giftCount, addedCount, awaitingPrice, orderCovered } = C.giftStatus(cart, config, rule);
      const now = this.ports.now?.() ?? Date.now();
      const waitKey = C.fingerprint(cart, config, rule);
      if (!awaitingPrice) this.priceWait = null;
      else if (this.priceWait?.key !== waitKey) this.priceWait = { key: waitKey, since: now };
      const priceStatus = awaitingPrice ? (now - this.priceWait.since >= 10000 ? 'discount' : 'pending') : '';
      this.latest = { ...summary, config, rule, gifts, giftCount, addedCount, orderCovered, status: status || priceStatus, cart,
        waived: Object.keys(this.ledger?.waived || {}).filter(s => Number(s) <= summary.entitled).length };
      this.ports.onState(this.latest);
    }
    async reconcile(refreshCart = true, transactionCart) {
      // Only the caller holding the shared lock may supply its just-returned cart.
      // Background sync always reads afresh; configuration is never cached across markets.
      const [config, initialCart] = await Promise.all([
        this.ports.getConfig(transactionCart), transactionCart || this.ports.getCart()
      ]);
      if (config.poolComplete === false) throw new Error('Gift pool exceeds the supported SKU, collection or product page size');
      let cart = initialCart;
      const rule = C.active(config, this.ports.now?.() ?? Date.now()) ? C.resolveRule(config, cart.currency) : null;
      const candidates = config.candidates || [];
      this.key = C.ledgerKey(cart, config, rule);
      this.ledger = this.load(this.key);
      for (const item of cart.items.filter(C.isManaged)) {
        if (item.properties._gift_campaign === config.campaign && item.properties._gift_rule === rule?.key) {
          this.ledger.assignments[C.slotOf(item)] = C.variantOf(item);
        }
      }
      this.persist();
      const stamp = C.fingerprint(cart, config, rule);
      let error = this.failures.get(stamp) || '';
      const previousPresentation = this.latest && C.presentation(this.latest.cart);
      this.emit(cart, config, rule, 'syncing');
      let changed = false;
      let batchAttempted = false;
      try {
        for (let step = 0; step < 100; step++) {
          // Use the latest full write response (including changed line keys).
          // Add responses are partial, so those still require a fresh cart read.
          if (C.fingerprint(cart, config, rule) !== stamp) { this.dirty = true; break; }
          const action = C.nextAction(cart, config, rule, this.ledger, candidates);
          if (action.type === 'settled') break;
          if (step === 99) throw new Error('Gift synchronization limit reached');
          if (action.type !== 'add') {
            const result = await this.ports.change(action.item.key, action.type === 'normalize' ? 1 : action.type === 'reduce' ? action.quantity : 0, action.item);
            cart = Array.isArray(result?.data?.items) && result.data.currency ? result.data : await this.ports.getCart();
            changed = true;
            continue;
          }
          if (error) break;
          if (this.ports.addMany && !batchAttempted) {
            batchAttempted = true;
            const planned = this.planBatch(cart, config, rule, candidates);
            if (planned.length > 1) {
              const quantities = new Map();
              for (const item of planned) quantities.set(String(item.id), (quantities.get(String(item.id)) || 0) + 1);
              // Check total demand per SKU, including existing paid/gift quantities.
              const available = await Promise.all([...quantities].map(([id, quantity]) =>
                this.ports.canAdd(candidates.find(c => String(c.id) === id), cart, config, quantity)));
              if (this.dirty) break;
              if (available.every(Boolean)) {
                for (const item of planned) this.ledger.assignments[item.properties._gift_slot] = String(item.id);
                this.persist();
                let batchError;
                try { await this.ports.addMany(planned); }
                catch (e) { batchError = e; }
                // Even an error can mean some/all lines were added. Never replay the batch.
                cart = await this.ports.getCart();
                const present = planned.filter(item => cart.items.some(line => C.isManaged(line) &&
                  line.properties._gift_campaign === config.campaign && line.properties._gift_rule === rule.key &&
                  C.slotOf(line) === Number(item.properties._gift_slot) && C.variantOf(line) === String(item.id)));
                changed = changed || present.length > 0;
                if (batchError && present.length !== planned.length && batchError.status !== 422) throw batchError;
                // Reconcile only missing slots; stock races fall back to individual selection.
                continue;
              }
              // Insufficient aggregate stock: single-slot fallback can use partial stock
              // and draw alternatives instead of rejecting every available gift.
            }
          }
          let available = [...candidates];
          let selected = available.find(c => String(c.id) === String(this.ledger.assignments[action.slot]));
          let added = false;
          while (available.length) {
            selected = selected || available[C.randomIndex(available.length, this.ports.random)];
            available = available.filter(c => String(c.id) !== String(selected.id));
            if (!await this.ports.canAdd(selected, cart, config)) { selected = null; continue; }
            // A user cart event during the inventory request invalidates this decision.
            if (this.dirty) break;
            this.ledger.assignments[action.slot] = String(selected.id);
            this.persist();
            try {
              await this.ports.add(selected.id, {
                _gift_source: C.SOURCE, _gift_campaign: config.campaign,
                _gift_slot: String(action.slot), _gift_rule: rule.key
              });
              changed = true; added = true;
              cart = await this.ports.getCart();
              break;
            } catch (e) {
              // A timed-out POST may have succeeded. Never blindly retry another gift.
              cart = await this.ports.getCart();
              if (cart.items.some(i => C.isManaged(i) && i.properties._gift_campaign === config.campaign &&
                  i.properties._gift_rule === rule.key && C.slotOf(i) === action.slot)) { changed = true; added = true; break; }
              if (e.status !== 422) throw e;
              selected = null;
            }
          }
          if (this.dirty) break;
          if (!added) { error = 'stock'; this.failures.set(stamp, error); break; }
        }
        if (this.dirty) cart = await this.ports.getCart();
        this.emit(cart, config, rule, error);
      } finally {
        if (refreshCart && (changed || (previousPresentation && previousPresentation !== C.presentation(cart)))) await this.ports.onCart(cart);
      }
    }
    async beforeCheckout() {
      await this.request();
      // Reconcile gift quantities/region only. Regios owns the checkout price.
      const cart = await this.ports.getCart();
      const { config, rule } = this.latest;
      const action = C.nextAction(cart, config, rule, this.ledger, config.candidates);
      if (action.type !== 'settled' && action.type !== 'add') throw new Error('Please refresh your cart before checking out.');
      return true;
    }
  };
});
